import {Component, OnDestroy, OnInit, TemplateRef} from '@angular/core';
import {TaskOptionsService} from '../../../../services/task-options.service';
import {CrmService} from '../../../../services/api/crm.service';
import {DepartmentsService} from '../../../../services/api/departments.service';
import {AlertService} from '../../../../services/notify/alert.service';
import {MemberService} from '../../../../services/member.service';
import {MemberModel} from '../../../../models/user.model';
import {HelpersService} from '../../../../services/helpers.service';
import {ModalService} from '../../../../services/modal/modal.service';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {Subscription} from 'rxjs';
import {PermissionsBoardModel, TaskAccess} from '../../../../models/tasktable.model';
import {DepartmentModel, DivisionModel, StructureDepartmentModel} from '../../../../models/department.model';
import {TranslateService} from '@ngx-translate/core';

interface PermissionRequestModel extends TaskAccess {
    role_type: 'member' | 'department' | 'division';
    role_key: string | number;
}

@Component({
    selector: 'app-task-access',
    templateUrl: './task-access.component.html',
    styleUrls: ['./task-access.component.scss'],
})
export class TaskAccessComponent implements OnInit, OnDestroy {

    structure: {
        is_editing: boolean,
        right: 'is_read_my_tasks' |
            'is_read_all_tasks' |
            'is_create_new_task' |
            'is_create_new_comment' |
            'is_set_property_val' |
            'is_edit_settings' |
            'is_set_self_in_task' |
            'is_set_other_in_task',
        title: string,
    }[] = [];

    departmentBoardRights: PermissionsBoardModel[] = [];
    userBoardRights: PermissionsBoardModel[] = [];
    divisionBoardRights: PermissionsBoardModel[] = [];

    copyPermissions: {
        departments: PermissionsBoardModel[],
        divisions: PermissionsBoardModel[],
        members: PermissionsBoardModel[],
    } = {
        departments: [],
        divisions: [],
        members: [],
    };

    allDepartments: StructureDepartmentModel[] = [];
    selectedDepartmentWithDivisions: StructureDepartmentModel;
    divisionSlider: {
        [key: number]: boolean
    } = {};
    allMembers: MemberModel[] = [];

    isLockButton = false;

    roleType: 'member' | 'department' | 'division';
    roleKey: string | number;

    subs: Subscription;

    editedPermissions: {
        [key: string]: PermissionRequestModel
    } = {};

    currentMember: MemberModel;

    constructor(
        public taskOptionsService: TaskOptionsService,
        public crmService: CrmService,
        public departmentsApi: DepartmentsService,
        public alert: AlertService,
        public memberService: MemberService,
        public helper: HelpersService,
        public modalService: ModalService,
        public translate: TranslateService,
    ) {
    }

    ngOnInit(): void {
        this.memberService.getCurrentMember().then(res => {
            this.currentMember = res;
        });
        if (this.taskOptionsService.selectedBoard) {
            this.getBoardAccess();
        }

        this.subs = this.taskOptionsService.emitChangeBoard.subscribe(() => {
            this.getBoardAccess();
        });
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
        this.taskOptionsService.isPermissionsChanged = false;
    }

    getBoardAccess(reload = false): void {
        this.taskOptionsService.getBoardRights(reload).then(res => {
            this.structure = res.structure;

            this.departmentBoardRights = res.departments;
            this.copyPermissions.departments = structuredClone(res.departments);

            this.userBoardRights = res.members;
            this.copyPermissions.members = structuredClone(res.members);

            this.divisionBoardRights = res.divisions;
            this.copyPermissions.divisions = structuredClone(res.divisions);
        });
    }

    async openCreateModal(roleType: 'division' | 'department' | 'member', content: TemplateRef<any>): Promise<void> {
        this.roleType = roleType;
        if (roleType === 'member') {
            await this.getAllUsers();
        } else {
            await this.getAllDepartments();
        }
        this.modalService.openTemplateModal(content);
    }

    async getAllDepartments(): Promise<void> {
        return new Promise(resolve => {
            this.departmentsApi.getStructureDepartments().subscribe({
                next: res => {
                    this.allDepartments = res.departments;
                    resolve();
                }
            });
        });
    }

    async getAllUsers(): Promise<void> {
        return new Promise(resolve => {
            this.memberService.getAllMembers().then(res => {
                this.allMembers = res.filter(x => x.project_setting.observation_type !== 'blocked' && !x.project_setting.is_project_admin);
                resolve();
            });
        });
    }

    deleteAccess(access: any): void {
        let alert: string;
        if (access.role_type === 'department') {
            alert = this.translate.instant('CONFIRM.TEXT.DELETE_DEPARTMENT_ACCESS', {name: access.role.name});
        } else if (access.role_type === 'member') {
            alert = this.translate.instant('CONFIRM.TEXT.DELETE_MEMBER_ACCESS', {name: this.helper.fullName(access.role)});
        } else {
            alert = this.translate.instant('CONFIRM.TEXT.DELETE_DIVISION_ACCESS', {name: access.role.name});
        }

        const modalSettings = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.DELETE'),
            modalBody: alert,
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
            buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO')
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then(data => {
            if (data) {
                const deletePermission = {
                    role_type: access.role_type,
                    role_key: access.role.id ? access.role.id : access.role.guid,
                };
                this.crmService.deleteBoardsRights(this.taskOptionsService.selectedBoard.alias, deletePermission).subscribe({
                    next: () => {
                        this.alert.setAlert(this.translate.instant('ALERTS.SUCCESS'), 'green');
                        this.getBoardAccess(true);
                    }, error: err => {
                        this.alert.setAlert(err.message, 'blue');
                    }
                });
            }
        }).catch(() => {
        });
    }

    addPermissionsToBoard(
        readAllTasks: string | 'succession' | 'allowed' | 'denied',
        createNewTask: string | 'succession' | 'allowed' | 'denied',
        createNewComment: string | 'succession' | 'allowed' | 'denied',
        setProperty: string | 'succession' | 'allowed' | 'denied',
        editBoard: string | 'succession' | 'allowed' | 'denied',
        setSelfInTask: string | 'succession' | 'allowed' | 'denied',
        setOtherInTask: string | 'succession' | 'allowed' | 'denied',
        modal: NgbModalRef
    ): void {
        const data = {
            role_type: this.roleType,
            role_key: this.roleKey,
            is_read_all_tasks: readAllTasks,
            is_create_new_task: createNewTask,
            is_create_new_comment: createNewComment,
            is_set_property_val: setProperty,
            is_edit_settings: editBoard,
            is_set_self_in_task: setSelfInTask,
            is_set_other_in_task: setOtherInTask
        };
        this.crmService.setBoardsRights(this.taskOptionsService.selectedBoard.alias, data).subscribe({
            next: () => {
                this.getBoardAccess(true);
                this.alert.setAlert(this.translate.instant('ALERTS.SUCCESS'), 'green');
                this.selectedDepartmentWithDivisions = undefined;
                this.roleKey = undefined;
                modal.close();
            }, error: err => {
                this.alert.setAlert(err.message, 'blue');
            }
        });
    }

    changePermissions(
        permission: PermissionsBoardModel,
        key: 'is_create_new_task' |
            'is_read_all_tasks' |
            'is_create_new_comment' |
            'is_set_property_val' |
            'is_edit_settings' |
            'is_read_my_tasks' |
            'is_set_self_in_task' |
            'is_set_other_in_task',
        value: string | 'succession' | 'allowed' | 'denied'
    ): void {
        this.taskOptionsService.isPermissionsChanged = true;
        const editingPermission = permission;
        const currentRoleKey = editingPermission.role_type === 'member' ?
            (editingPermission.role as MemberModel).guid : (editingPermission.role as DepartmentModel).id;
        editingPermission[key] = value;

        if (!this.editedPermissions[currentRoleKey]) {
            const data = {
                role_type: editingPermission.role_type,
                role_key: currentRoleKey,
                is_read_all_tasks: editingPermission.is_read_all_tasks,
                is_create_new_task: editingPermission.is_create_new_task,
                is_create_new_comment: editingPermission.is_create_new_comment,
                is_set_property_val: editingPermission.is_set_property_val,
                is_edit_settings: editingPermission.is_edit_settings,
                is_set_self_in_task: editingPermission.is_set_self_in_task,
                is_set_other_in_task: editingPermission.is_set_other_in_task,
            };
            this.editedPermissions[currentRoleKey] = structuredClone(data);
        } else {
            const checkChanges: PermissionsBoardModel = this.copyPermissions[editingPermission.role_type + 's']
                .find((copyPermission: PermissionsBoardModel) => {
                    const permissionKey = copyPermission.role_type === 'member' ?
                        (copyPermission.role as MemberModel).guid : (copyPermission.role as DepartmentModel).id;
                    if (permissionKey === currentRoleKey) {
                        return copyPermission;
                    }
                });

            this.editedPermissions[currentRoleKey][key] = value;
            if (checkChanges.is_create_new_task === this.editedPermissions[currentRoleKey].is_create_new_task &&
                checkChanges.is_read_all_tasks === this.editedPermissions[currentRoleKey].is_read_all_tasks &&
                checkChanges.is_edit_settings === this.editedPermissions[currentRoleKey].is_edit_settings &&
                checkChanges.is_create_new_comment === this.editedPermissions[currentRoleKey].is_create_new_comment &&
                checkChanges.is_set_property_val === this.editedPermissions[currentRoleKey].is_set_property_val &&
                checkChanges.is_set_other_in_task === this.editedPermissions[currentRoleKey].is_set_other_in_task &&
                checkChanges.is_set_self_in_task === this.editedPermissions[currentRoleKey].is_set_self_in_task
            ) {
                delete this.editedPermissions[currentRoleKey];

                if (!Object.keys(this.editedPermissions).length) {
                    this.taskOptionsService.isPermissionsChanged = false;
                }
            }
        }
    }

    async savePermissions(): Promise<void> {
        await this.saveRequestsController().then(() => {
            this.alert.setAlert(this.translate.instant('ALERTS.SUCCESS'), 'green');
        }).catch(() => {
            this.alert.setAlert(this.translate.instant('ALERTS.ERROR'), 'blue');
        });

        this.taskOptionsService.isPermissionsChanged = false;
        this.editedPermissions = {};
        this.getBoardAccess();
    }

    async saveRequestsController(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.saveRequests().then(() => {
                resolve();
            }).catch(() => {
                reject();
            });
        });
    }

    saveRequests(): Promise<void> {
        return new Promise((resolve, reject) => {
            Object.keys(this.editedPermissions).forEach(key => {
                const data = this.editedPermissions[key];

                this.crmService.setBoardsRights(this.taskOptionsService.selectedBoard.alias, data).subscribe({
                    next: () => {
                        resolve();
                    },
                    error: () => {
                        reject();
                    }
                });
            });
        });
    }

    cancelEditPermissions(): void {
        this.editedPermissions = {};
        this.taskOptionsService.isPermissionsChanged = false;
        this.userBoardRights = structuredClone(this.copyPermissions.members);
        this.divisionBoardRights = structuredClone(this.copyPermissions.divisions);
        this.departmentBoardRights = structuredClone(this.copyPermissions.departments);
    }

    openPermission(permission: PermissionsBoardModel): void {
        if (permission.isOpen) {
            permission.isOpen = !permission.isOpen;
            return;
        }
        if (permission.role_type === 'department') {
            this.departmentsApi.getDepartmentUsers((permission.role as DepartmentModel).id).subscribe({
                next: res => {
                    permission.members = res.users.map(user => user.member);
                    permission.isOpen = !permission.isOpen;
                }, error: err => {
                    this.alert.setAlert(err.message, 'blue');
                }
            });
        } else {
            this.departmentsApi.getDepartmentDivisions((permission.role as DivisionModel).department.id).subscribe({
                next: res => {
                    permission.members = (permission.role as DivisionModel).members_guids.map(memberGuid =>
                        res.department.members[memberGuid]);
                    permission.isOpen = !permission.isOpen;
                }, error: err => {
                    this.alert.setAlert(err.message, 'blue');
                }
            });
        }
    }
}
